import React, {useMemo, useState} from 'react';
import {Modal, TouchableOpacity, Text, View, ActivityIndicator, DeviceEventEmitter, Alert} from 'react-native';
import {useTheme} from '~contexts/ThemeContext';
import {useTranslation} from 'react-i18next';
import {widthPercentageToDP as wp, heightPercentageToDP as hp} from 'react-native-responsive-screen';
import {NavigationProps} from '~types/navigation/navigation.type';
import {useNavigation} from '@react-navigation/native';
import {SCREENS} from '~constants';
import {useGetContactChat} from '~hooks/chats/useGetContactChat';
import FirebaseChatsService from '~services/FirebaseChats';
import {useGetUserAccount} from '~hooks/user/useGetUser';
import ModernTextInput from '~components/ModernTextInput';
import ModernButton from '~components/ModernButton';
import axios from 'axios';
import {BASE_API_URL} from '@env';
import {useUserStore} from '~providers/userStore/zustand';
import {Notifier, NotifierComponents} from 'react-native-notifier';
import useGetPyxiSelectAllData from '~hooks/pyxiselect/useGetPyxiSelectAllData';

interface NeighbourhoodRestrictionModalProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * NeighbourhoodRestrictionModal Component
 *
 * This modal is displayed when a user tries to access neighbourhood events
 * but is not joined to any neighbourhood groups. It provides an option
 * to chat with support to join a group.
 *
 * @param visible - Controls the visibility of the modal
 * @param onClose - Callback function to close the modal
 */
const NeighbourhoodRestrictionModal: React.FC<NeighbourhoodRestrictionModalProps> = ({visible, onClose}) => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const navigation = useNavigation<NavigationProps>();
  const {contactChat, isLoading: isContactChatLoading} = useGetContactChat();
  const {
    data: pyxiSelectData,
    isLoading: isPyxiSelectLoading,
    error: pyxiSelectError,
    refetch: refetchPyxiSelectData,
  } = useGetPyxiSelectAllData();
  const user = useUserStore(state => state.user);
  const uid = user?.uid;
  const {data: userAccount, isLoading: isUserAccountLoading} = useGetUserAccount(uid || '');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  // Transform API data to journey items
  const neighborhoodPercentage = useMemo(() => {
    if (!pyxiSelectData || isPyxiSelectLoading) return [];

    const calculateCompletionPercentage = (filled: string[], total: string[]) => {
      if (total.length === 0) return 100;
      return Math.round((filled.length / total.length) * 100);
    };

    // Neighbourhood Match
    const neighborhoodFilled = pyxiSelectData.neighborhood.status.filled;
    const neighborhoodTotal = [
      ...pyxiSelectData.neighborhood.status.filled,
      ...pyxiSelectData.neighborhood.status.not_filled,
    ];
    const neighborhoodPct = calculateCompletionPercentage(neighborhoodFilled, neighborhoodTotal);

    return neighborhoodPct;
  }, [pyxiSelectData, isPyxiSelectLoading, pyxiSelectError]);

  // Support user information (hardcoded since the user doesn't exist in database)
  const supportUser = {
    uid: 'CmCCvWvpRVhhFlHdgU9QE6hbun82',
    first_name: 'The Maida Vale',
    last_name: 'Neighbourhood Initiative',
    photo:
      'https://firebasestorage.googleapis.com/v0/b/pyxida-e6211.appspot.com/o/default%2Fsupport-avatar.png?alt=media',
  };

  // Check if any required data is still loading
  const isDataLoading = isContactChatLoading || isUserAccountLoading;

  const handleChatWithUs = async () => {
    setIsSendingMessage(true);

    try {
      let chatId: string | undefined = contactChat?.key;
      let image = '';
      let userName = '';

      // Debug: Log the required data
      console.log('Debug - Required data check:', {
        uid,
        supportUserUid: supportUser?.uid,
        userAccountUid: userAccount?.uid,
        hasContactChat: !!contactChat,
        isDataLoading,
      });

      // If private chat doesn't exist, create it first
      if (!contactChat && uid && userAccount?.uid) {
        console.log('Creating private chat for new user...');

        // Validate and prepare user data
        const user_name1 = `${userAccount.first_name || ''} ${userAccount.last_name || ''}`.trim();
        const user_name2 = `${supportUser.first_name} ${supportUser.last_name}`;
        const user_image =
          userAccount.photo ||
          'https://firebasestorage.googleapis.com/v0/b/pyxida-e6211.appspot.com/o/default%2Fsupport-avatar.png?alt=media';

        // Debug: Log the data being passed
        console.log('Debug - createPrivateChat parameters:', {
          user_id1: uid,
          user_id2: supportUser.uid,
          user_name1,
          user_name2,
          user_image,
          userAccount: {
            first_name: userAccount.first_name,
            last_name: userAccount.last_name,
            photo: userAccount.photo,
          },
        });

        // Validate required fields
        if (!user_name1 || user_name1 === '') {
          console.error('user_name1 is empty or undefined');
          navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
          setIsSendingMessage(false);
          onClose();
          return;
        }

        if (!user_image || user_image === '') {
          console.error('user_image is empty or undefined');
          navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
          setIsSendingMessage(false);
          onClose();
          return;
        }

        // Additional validation for all fields
        const params = {
          user_id1: uid,
          user_id2: supportUser.uid,
          user_name1,
          user_name2,
          user_image,
        };

        console.log('Debug - Final parameters before API call:', params);
        console.log('Debug - Parameter types:', {
          user_id1: typeof params.user_id1,
          user_id2: typeof params.user_id2,
          user_name1: typeof params.user_name1,
          user_name2: typeof params.user_name2,
          user_image: typeof params.user_image,
        });

        // Check for any undefined values
        const hasUndefined = Object.values(params).some(value => value === undefined);
        if (hasUndefined) {
          console.error('Found undefined values in parameters:', params);
          navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
          setIsSendingMessage(false);
          onClose();
          return;
        }

        try {
          const newChatId = await FirebaseChatsService.createPrivateChat(params);

          if (newChatId) {
            console.log('Private chat created successfully:', newChatId);
            chatId = newChatId;
            // Set default values for new chat
            image = supportUser.photo;
            userName = user_name2;
          } else {
            console.error('Failed to create private chat - returned empty string');
            // Fallback navigation if chat creation fails
            navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
            setIsSendingMessage(false);
            onClose();
            return;
          }
        } catch (error) {
          console.error('Error in createPrivateChat:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          // Fallback navigation if chat creation fails
          navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
          setIsSendingMessage(false);
          onClose();
          return;
        }
      } else if (contactChat) {
        // Extract the interlocutor information from existing chat
        const interlocutorId = contactChat?.userIds?.find(userId => userId !== uid);
        const interlocutorMessage = contactChat?.history?.find(message => message.sender_id === interlocutorId);
        const interlocutorImage = interlocutorMessage?.sender_image;

        image = contactChat?.eventImage || interlocutorImage || supportUser.photo;
        const userIndex =
          contactChat?.userIds?.findIndex(user => user.toLowerCase() !== (uid || '').toLowerCase()) || 0;
        userName = contactChat?.users?.[userIndex] || `${supportUser.first_name} ${supportUser.last_name}`;
      } else {
        // No existing chat and couldn't create new one (missing required data)
        console.error('Missing required data to create or find private chat:', {
          uid: !!uid,
          userAccountUid: !!userAccount?.uid,
          userAccount: userAccount,
        });

        // Wait a bit for data to load if still loading
        if (isDataLoading) {
          console.log('Still loading data, waiting...');
          setTimeout(() => {
            setIsSendingMessage(false);
          }, 1000);
          return;
        }

        navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
        setIsSendingMessage(false);
        onClose();
        return;
      }

      // Send a default message to the private chat
      if (chatId) {
        if (neighborhoodPercentage == 100) {
          //
          try {
            await FirebaseChatsService.pushMessageForUsers({
              chat_id: chatId,
              user_id: supportUser.uid || '',
              user_name: `${supportUser?.first_name || ''} ${supportUser?.last_name || ''}`.trim(),
              user_image: supportUser?.photo || '',
              text: `Hi ${userAccount?.first_name || ''}, how are you today? Thanks for contacting Pyxi!`,
            });

            await FirebaseChatsService.pushMessageForUsers({
              chat_id: chatId,
              user_id: supportUser.uid || '',
              user_name: `${supportUser?.first_name || ''} ${supportUser?.last_name || ''}`.trim(),
              user_image: supportUser?.photo || '',
              text: `I can see you’ve already completed the neighbourhood quiz, so we have your postcode and everything else we need for now. We will get in touch when your neighbourhood is ready to launch 🧡`,
            });

            await FirebaseChatsService.pushMessageForUsers({
              chat_id: chatId,
              user_id: supportUser.uid || '',
              user_name: `${supportUser?.first_name || ''} ${supportUser?.last_name || ''}`.trim(),
              user_image: supportUser?.photo || '',
              text: `Is there anything else you wanted to chat about with us today?`,
            });
          } catch (error) {
            console.error('Error sending default message:', error);
            // Continue with navigation even if message sending fails
          }
          setIsSendingMessage(false);
          onClose();
          navigation.navigate(SCREENS.CHAT_STACK, {
            key: chatId,
            image: image,
            userName: userName,
          });
          return;
        } else {
          try {
            await FirebaseChatsService.pushMessageForUsers({
              chat_id: chatId,
              user_id: supportUser.uid || '',
              user_name: `${supportUser?.first_name || ''} ${supportUser?.last_name || ''}`.trim(),
              user_image: supportUser?.photo || '',
              text: `Hi ${userAccount?.first_name || ''}, how are you today? Thanks for contacting Pyxi!`,
            });

            await FirebaseChatsService.pushMessageForUsers({
              chat_id: chatId,
              user_id: supportUser.uid || '',
              user_name: `${supportUser?.first_name || ''} ${supportUser?.last_name || ''}`.trim(),
              user_image: supportUser?.photo || '',
              text: `We’d be delighted to add you to the neighbourhood waitlist 🧡. To do this, please help us by completing our neighbourhood quiz https://www.pyxi.ai/pyxi-select-quiz?form_type=neighborhood`,
            });
            navigation.navigate(SCREENS.CHAT_STACK, {
              key: chatId,
              image: image,
              userName: userName,
            });
            setIsSendingMessage(false);
            onClose();
            return;
          } catch (error) {
            console.error('Error sending default message:', error);
          }
        }

        // Navigate to the chat stack with the private chat parameters
        navigation.navigate(SCREENS.CHAT_STACK, {
          key: chatId,
          image: image,
          userName: userName,
        });
      }
    } catch (error) {
      console.error('Error in handleChatWithUs:', error);
      // Fallback navigation
      navigation.navigate(SCREENS.CHAT_STACK, {chatType: 'support'});
    }

    setIsSendingMessage(false);
    onClose();
  };

  const joinGroup = async () => {
    try {
      setIsJoining(true);
      const token = useUserStore.getState().token;
      const config = {
        headers: {Authorization: token, Accept: 'application/json'},
      };
      await axios.post(BASE_API_URL + 'community/join', {invitation_code: invitationCode}, config);
      Notifier.showNotification({
        title: t('settings.join_success'),
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'success',
        },
      });
      DeviceEventEmitter.emit('onGroupJoin');
      setInvitationCode('');
    } catch (error) {
      Notifier.showNotification({
        title: t('settings.join_error'),
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'error',
        },
      });
    } finally {
      setIsJoining(false);
      onClose();
    }
  };

  return (
    <Modal visible={visible} transparent={true} animationType="fade">
      <TouchableOpacity
        onPress={onClose}
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: colors.overlayBackground,
        }}>
        <TouchableOpacity
          activeOpacity={1}
          onPress={e => {
            e.isPropagationStopped;
          }}
          style={{
            width: wp('85%'),
            backgroundColor: colors.white,
            borderRadius: 15,
            padding: hp('3%'),
            alignItems: 'center',
          }}>
          <Text
            style={{
              fontSize: hp('2.5%'),
              fontWeight: 'bold',
              marginBottom: hp('2%'),
              textAlign: 'center',
              color: colors.textPrimary,
            }}>
            {t('events.neighbourhood_restriction_title') || 'Neighbourhood Groups Access'}
          </Text>
          <Text
            style={{
              fontSize: hp('1.8%'),
              color: colors.textSecondary,
              marginBottom: hp('1.5%'),
              textAlign: 'center',
              lineHeight: 24,
            }}>
            {t('events.neighbourhood_restriction_message') ||
              'Oops! You don’t seem to be a member of any neighbourhood group! Message us below to request access and check out some neighbourhood events'}
          </Text>
          <View style={{flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: hp('2%')}}>
            <View style={{flex: 1, marginRight: 8}}>
              <ModernTextInput
                placeholder={t('settings.enter_code') || 'Enter code'}
                value={invitationCode}
                onChangeText={setInvitationCode}
                variant="outlined"
                size="md"
                style={{marginBottom: 0}}
              />
            </View>
            <ModernButton
              title={t('settings.join') || 'Join'}
              onPress={joinGroup}
              loading={isJoining}
              disabled={!invitationCode || isJoining}
              variant="primary"
              size="md"
              style={{minWidth: 90, marginTop: 10}}
            />
          </View>
          <TouchableOpacity
            onPress={handleChatWithUs}
            disabled={isSendingMessage || isDataLoading}
            style={{
              backgroundColor: colors.secondary,
              paddingVertical: hp('1.5%'),
              paddingHorizontal: hp('4%'),
              borderRadius: 25,
              alignItems: 'center',
              marginBottom: hp('1%'),
              flexDirection: 'row',
              minWidth: wp('40%'),
              justifyContent: 'center',
            }}>
            {isSendingMessage && <ActivityIndicator size="small" color={colors.white} style={{marginRight: 8}} />}
            <Text
              style={{
                color: colors.white,
                fontSize: hp('2%'),
                fontWeight: 'bold',
              }}>
              {isSendingMessage
                ? t('generic.sending') || 'Sending...'
                : isDataLoading
                  ? t('generic.loading') || 'Loading...'
                  : t('events.chat_with_us') || 'Chat with us'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onClose}
            disabled={isSendingMessage}
            style={{
              paddingVertical: hp('1%'),
              paddingHorizontal: hp('3%'),
            }}>
            <Text
              style={{
                color: colors.textSecondary,
                fontSize: hp('1.8%'),
                opacity: isSendingMessage ? 0.5 : 1,
              }}>
              {t('generic.cancel') || 'Cancel'}
            </Text>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

export default NeighbourhoodRestrictionModal;
